/**
 * Public (unauthenticated) lookups used by the onboarding waiting-room pages
 * (`/wc-invite/[code]`, `/org/[slug]`). These endpoints are rate-limited and
 * return only display-safe fields.
 */

import axios from 'axios';
import { buildApiUrl, API_ENDPOINTS } from '../../../constants/api';

export interface PublicSquadLookupResponse {
  groupId: string;
  name: string;
  description: string | null;
  memberCount: number;
  customLogoUrl: string | null;
  companyBranding: {
    companyName: string;
    primaryColor: string;
    secondaryColor: string | null;
    logoUrl: string | null;
    bannerUrl: string | null;
    monkeyIdleUrl: string | null;
  } | null;
  orgSlug: string | null;
  isOrgDefault: boolean;
  /**
   * The competition the squad plays (specs/champions-league Story 20). Optional: the server
   * ships ahead of the app, so an older response simply omits it and the invite card drops the
   * stats it cannot fill rather than showing another competition's numbers.
   */
  competition?: PublicCompetitionPreview | null;
}

/** Display-safe competition facts for the invite ticket. Any unknown number arrives as `null`. */
export interface PublicCompetitionPreview {
  id: string;
  /** Operator display name, e.g. "Champions 26/27". */
  name: string;
  /** UPCOMING | LIVE | FINISHED | ARCHIVED. */
  lifecycle: string;
  /** Non-voided fixtures linked to the competition; `null` when none are linked yet. */
  matchCount: number | null;
  /** First kickoff, i.e. when picks lock. ISO string. */
  locksAt: string | null;
}

export interface PublicOrgLookupResponse {
  name: string;
  displayName: string | null;
  slug: string;
  logoUrl: string | null;
  bannerUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
  description: string | null;
  defaultGroup: {
    id: string;
    inviteCode: string;
    name: string;
  } | null;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Resolves a squad by invite code within a competition (no auth required).
 * Used by `/wc-invite/[code]` and the join screen to show the preview card before
 * sign-up. Invite codes are unique per competition, so the id is never optional —
 * the WC-bound convenience wrapper was removed for exactly that reason.
 */
export async function fetchPublicSquadByCode(
  competitionId: string,
  code: string,
): Promise<PublicSquadLookupResponse> {
  const path = API_ENDPOINTS.competitions.groupByInviteCodePublic
    .replace(':competitionId', encodeURIComponent(competitionId))
    .replace(':code', encodeURIComponent(code));
  const url = buildApiUrl(path);
  const response = await axios.get<ApiEnvelope<PublicSquadLookupResponse>>(url, { timeout: 10000 });
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.error || 'Squad not found');
  }
  return response.data.data;
}

/**
 * Resolves an organization by slug (no auth required).
 * Used by `/org/[slug]` to render the branded landing.
 */
export async function fetchPublicOrgBySlug(slug: string): Promise<PublicOrgLookupResponse> {
  const path = API_ENDPOINTS.accounts.bySlugPublic.replace(':slug', encodeURIComponent(slug));
  const url = buildApiUrl(path);
  const response = await axios.get<ApiEnvelope<PublicOrgLookupResponse>>(url);
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.error || 'Organization not found');
  }
  return response.data.data;
}

/**
 * Resolves an organization by join code (no auth required).
 * Used by `/join?join=CODE` to render org branding before the user signs in.
 */
export async function fetchPublicOrgByJoinCode(code: string): Promise<PublicOrgLookupResponse> {
  const path = API_ENDPOINTS.accounts.byJoinCodePublic.replace(':code', encodeURIComponent(code));
  const url = buildApiUrl(path);
  const response = await axios.get<ApiEnvelope<PublicOrgLookupResponse>>(url);
  if (!response.data?.success || !response.data.data) {
    throw new Error(response.data?.error || 'Organization not found');
  }
  return response.data.data;
}
